import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { deckKeys } from '@/entities/deck';
import { createDeck } from '@/shared/api/decks';
import type { Deck } from '@/shared/api/types';
import { Button, ErrorMessage, TextArea, TextField } from '@/shared/ui';

import styles from './DeckEditor.module.css';

/**
 * Creates a deck, like a new repository on GitHub: a name and an optional
 * description, everything else is added on the deck's own page.
 */
export function CreateDeckForm({
  onCreated,
}: {
  onCreated?: (deck: Deck) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const mutation = useMutation({
    mutationFn: () => createDeck({ title, description }),
    onSuccess: async (deck) => {
      await queryClient.invalidateQueries({ queryKey: deckKeys.all });
      onCreated?.(deck);
    },
  });

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <form className={styles.form} onSubmit={submit}>
      <TextField
        label={t('decks.name')}
        placeholder={t('decks.newPlaceholder')}
        required
        maxLength={200}
        autoFocus
        value={title}
        onChange={(event) => {
          setTitle(event.target.value);
        }}
      />
      <TextArea
        label={t('decks.descriptionOptional')}
        maxLength={2000}
        value={description}
        onChange={(event) => {
          setDescription(event.target.value);
        }}
      />
      {mutation.isError && <ErrorMessage error={mutation.error} />}
      <Button
        type="submit"
        variant="primary"
        className={styles.submit}
        disabled={mutation.isPending}
      >
        {t('decks.createDeck')}
      </Button>
    </form>
  );
}
